/* eslint-disable @typescript-eslint/no-explicit-any */
import { indexedDBManager } from '../utils/indexedDB';
import '../utils/debugReferenceSync';
import { API_ENDPOINTS, BASE_URL } from '../constants/apiEndpoints';
import { ENVIRONMENT_CONFIG } from '../constants/environmentConfig';
import { getLanguage } from '../helpers/fetcher';
import { TaskTypeOption } from '../pages/inspector/newInspections/components/CreateNewTask/AddTaskForm';
import { generateUniqueRefId } from '../utils/refIdGenerator';
import { ONLINE_FIRST_ENTITIES } from '../constants/configs';
import type { Basket, BasketData, BasketMainProduct } from '../types/basket';
import type { Inspection } from '../types/inspection';
import type { MainProduct } from '../types/mainProduct';
import type { Presentation } from '../types/presentation';
import type { Price } from '../types/price';
import type { ProductCategory } from '../types/productCategory';
import type {
  ConvertibleUnitGroup,
  Establishment,
  ReferenceDataEntity,
  ReferenceSyncConfig,
  SyncStatus,
} from '../types/referenceData';
import type { Task } from '../types/task';
import { PresentationStatusEnum } from '../enums/PresentationStatusEnum';

type SyncConfig = ReferenceSyncConfig;

export class ReferenceDataSyncService {
  private config: SyncConfig;
  private syncInterval: number | null = null;
  private isRunning = false;
  private readonly entities = [
    'task_type',
    'establishments',
    'establishmentTypes',
    'locations',
    'productCategories',
    'presentation',
    'basket',
    'inspection',
    'task',
    'mainProduct',
    'convertibleUnitGroups',
  ] as const;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  constructor(config: Partial<SyncConfig> = {}) {
    this.config = {
      baseUrl: config.baseUrl || BASE_URL.CONSUMER_PRICE_BASE,
      syncInterval: config.syncInterval || ENVIRONMENT_CONFIG.REFERENCE_SYNC_INTERVAL,
      retryAttempts: config.retryAttempts || 3,
      retryDelay: config.retryDelay || 5000,
    };
  }

  async init(): Promise<void> {
    // IndexedDBManager already initializes itself in constructor
    // No need to call init() again - it will auto-initialize when needed
  }

  async startAutoSync(): Promise<void> {
    if (this.isRunning) return;

    this.isRunning = true;

    if (navigator.onLine) {
      this.syncAllEntities().catch((error) => {
        console.warn('Initial reference data sync failed or timed out:', error);
      });
    }

    this.syncInterval = setInterval(async () => {
      if (navigator.onLine) {
        await this.syncAllEntities();
      }
    }, this.config.syncInterval);

    this.setupConnectionListeners();
  }

  async stopAutoSync(): Promise<void> {
    this.isRunning = false;
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    this.removeConnectionListeners();
  }

  async syncAllEntities(): Promise<SyncStatus[]> {
    if (!navigator.onLine) {
      return this.getSyncStatus();
    }

    const results = await Promise.all(
      this.entities.map(async (entity) => {
        try {
          return await this.syncEntity(entity);
        } catch (error) {
          return {
            entity,
            isSync: false,
            lastSyncTime: Date.now(),
            itemCount: 0,
            error: error instanceof Error ? error.message : 'Unknown error',
          };
        }
      })
    );

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('referenceDataSynced'));
    }

    return results;
  }

  async syncEntity(entity: string, forceFullSync = false): Promise<SyncStatus> {
    if (!navigator.onLine) {
      const existingData = await indexedDBManager.getReferenceData(entity);
      return {
        entity,
        isSync: false,
        lastSyncTime: 0,
        itemCount: existingData.length,
        error: 'Offline - using cached data',
      };
    }

    try {
      const currentSyncMap = await indexedDBManager.getSyncMap(entity);
      let sinceTime: number | undefined;

      if (!forceFullSync && currentSyncMap && currentSyncMap.lastSyncTime > 0) {
        sinceTime = currentSyncMap.lastSyncTime;
      }

      let allServerData: any[] = [];
      let currentPage = 1;
      let totalPages = 1;
      let hasMoreData = true;
      let lastServerResponse: any = null;

      const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      const pageSize = entity === 'task' ? 10 : 100;

      // Fetch paginated batches
      while (hasMoreData && currentPage <= totalPages) {
        const response = await this.fetchEntityDataWithPagination(
          entity,
          sinceTime,
          currentPage,
          pageSize
        );

        if (response) {
          lastServerResponse = response;
          if (response.data && response.data.length > 0) {
            allServerData = allServerData.concat(response.data);
            totalPages = response.totalPages || 1;
            currentPage++;

            if (response.data.length < pageSize) {
              hasMoreData = false;
            }
          } else {
            hasMoreData = false;
          }
        } else {
          hasMoreData = false;
        }

        if (hasMoreData) {
          await delay(20);
        }
      }

      // Process and store incoming data first
      allServerData = allServerData.map((item) => ({
        ...item,
        updateTime: item.updateTime != null ? Number(item.updateTime) : 0,
        createTime: item.createTime != null ? Number(item.createTime) : 0,
      }));

      if (allServerData.length > 0) {
        if (
          entity === 'presentation' ||
          entity === 'mainProduct' ||
          entity === 'basket' ||
          entity === 'price' ||
          entity === 'inspection' ||
          entity === 'task'
        ) {
          await this.storeServerDataAsLocal(entity, allServerData);
        } else {
          await indexedDBManager.storeReferenceData(entity, allServerData);
        }
      }
      // check whether there are missing data from server in indexeddb for main product
      if (entity === 'mainProduct' && lastServerResponse && currentSyncMap) {
        const existingLocalData = await indexedDBManager.getReferenceData(entity);
        const totalServerRecords = lastServerResponse?.absoluteTotal || 0;

        if (existingLocalData.length < totalServerRecords && totalServerRecords > 0) {
          if (!forceFullSync && currentSyncMap.lastSyncTime > 0) {
            await indexedDBManager.updateSyncMap(entity, {
              lastSyncTime: 0,
              syncStatus: 'pending',
            });

            // Recurse immediately with the full sync override flag
            return await this.syncEntity(entity, true);
          }
        }
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('referenceDataSynced'));
      }

      if (allServerData.length > 0) {
        const maxUpdateTime = Math.max(...allServerData.map((i) => Number(i.updateTime) || 0));

        await indexedDBManager.updateSyncMap(entity, {
          lastSyncTime: maxUpdateTime,
          lastSyncOperationTime: Date.now(),
          syncStatus: 'synced',
        });

        return {
          entity,
          isSync: true,
          lastSyncTime: maxUpdateTime,
          itemCount: allServerData.length,
        };
      } else {
        await indexedDBManager.updateSyncMap(entity, {
          lastSyncTime: currentSyncMap?.lastSyncTime || 0,
          lastSyncOperationTime: Date.now(),
          syncStatus: 'synced',
        });

        const existingData = await indexedDBManager.getReferenceData(entity);
        return {
          entity,
          isSync: true,
          lastSyncTime: currentSyncMap?.lastSyncTime || 0,
          itemCount: existingData.length,
        };
      }
    } catch (error) {
      throw error;
    }
  }

  private async fetchEntityDataWithPagination(
    entity: string,
    since?: number,
    page?: number,
    limit?: number
  ): Promise<{
    data: ReferenceDataEntity[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    absoluteTotal?: number;
  } | null> {
    try {
      const authToken = this.getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept-Language': getLanguage(),
      };

      let endpoint: string;

      if (entity === 'task') {
        endpoint = `/${API_ENDPOINTS.TASK.GET_TASK_LIST}`;
      } else {
        const usePublicEndpoint =
          !authToken && ['basket', 'locations', 'presentation'].includes(entity);
        endpoint = usePublicEndpoint
          ? this.getPublicEndpointForEntity(entity)
          : this.getEndpointForEntity(entity);
      }

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const targetBaseUrl = this.config.baseUrl;

      const url = new URL(`${targetBaseUrl}${endpoint}`);

      if (since) {
        url.searchParams.append('since', since.toString());
      }
      if (page) {
        url.searchParams.append('page', page.toString());
      }
      if (limit) {
        url.searchParams.append('limit', limit.toString());
      }
      url.searchParams.append('sortBy', 'updateTime');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);

      let response: Response;
      try {
        response = await fetch(url.toString(), {
          method: 'GET',
          headers,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch ${entity}: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        return {
          data,
          total: data.length,
          page: page || 1,
          limit: limit || 100,
          totalPages: 1,
        };
      } else if (data?.data?.data && Array.isArray(data?.data?.data)) {
        return {
          data: data?.data?.data,
          total: data?.data?.total || data?.data?.data?.length,
          page: data?.data?.page || page || 1,
          limit: data?.data?.limit || limit || 100,
          totalPages:
            data?.data?.totalPages ||
            Math.ceil(
              (data?.data?.total || data?.data?.data.length) / (data?.data?.limit || limit || 100)
            ),
          absoluteTotal:
            data?.data?.absoluteTotal !== undefined
              ? data?.data?.absoluteTotal
              : data?.data?.total || data?.data?.data.length,
        };
      } else if (data?.data && Array.isArray(data?.data)) {
        return {
          data: data?.data,
          total: data?.total || data?.data?.length,
          page: data?.page || page || 1,
          limit: data?.limit || limit || 100,
          totalPages:
            data?.totalPages ||
            Math.ceil((data?.total || data?.data.length) / (data?.limit || limit || 100)),
          absoluteTotal:
            data?.absoluteTotal !== undefined
              ? data?.absoluteTotal
              : data?.total || data?.data.length,
        };
      } else {
        return {
          data: [],
          total: 0,
          page: page || 1,
          limit: limit || 100,
          totalPages: 1,
        };
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error(`Failed to fetch ${entity} reference data`);
    }
  }

  private getAuthToken(): string | null {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      return token || null;
    } catch {
      return null;
    }
  }

  private readonly lastFullSyncTimeStorageKey = 'referenceData_lastFullSyncTime';

  private getLastFullSyncTime(): number {
    try {
      return Number(localStorage.getItem(this.lastFullSyncTimeStorageKey)) || 0;
    } catch {
      return 0;
    }
  }

  private setLastFullSyncTime(time: number): void {
    try {
      localStorage.setItem(this.lastFullSyncTimeStorageKey, String(time));
    } catch {
      // ignore storage errors
    }
  }

  private getEndpointForEntity(entity: string): string {
    switch (entity) {
      case 'establishments':
        return `/${API_ENDPOINTS.ESTABLISHMENT.GET_ESTABLISHMENT_LIST}`;
      case 'establishmentTypes':
        return `/${API_ENDPOINTS.ESTABLISHMENT_TYPE.GET_ESTABLISHMENT_TYPE_LIST}`;
      case 'locations':
        return `/${API_ENDPOINTS.LOCATION.GET_LOCATION_LIST}`;
      case 'productCategories':
        return `/${API_ENDPOINTS.PRODUCT_CATEGORY.GET_PRODUCT_CATEGORY_LIST}`;
      case 'presentation':
        return `/${API_ENDPOINTS.PRESENTATION.GET_PRESENTATION_LIST}`;
      case 'basket':
        return `/${API_ENDPOINTS.BASKET.GET_BASKET_LIST}`;
      case 'price':
        return `/${API_ENDPOINTS.PRICE.GET_PRICE_LIST}`;
      case 'inspection':
        return `/${API_ENDPOINTS.INSPECTION.GET_INSPECTION_LIST}`;
      case 'task':
        return `/${API_ENDPOINTS.TASK.GET_TASK_LIST}`;
      case 'task_type':
        return `/${API_ENDPOINTS.TASK.GET_TASK_TYPE_LIST}`;
      case 'mainProduct':
        return `/${API_ENDPOINTS.MAIN_PRODUCT.GET_MAIN_PRODUCT_LIST}`;
      case 'convertibleUnitGroups':
        return `/${API_ENDPOINTS.PRESENTATION.GET_CONVERTIBLE_UNIT_GROUPS}`;
      default:
        throw new Error(`Unknown entity: ${entity}`);
    }
  }

  private getPublicEndpointForEntity(entity: string): string {
    switch (entity) {
      case 'basket':
        return `/${API_ENDPOINTS.BASKET.GET_PUBLIC_BASKETS}`;
      case 'locations':
        return `/${API_ENDPOINTS.LOCATION.GET_PUBLIC_LOCATIONS}`;
      case 'presentation':
        return `/${API_ENDPOINTS.PRESENTATION.GET_PUBLIC_PRESENTATIONS}`;
      default:
        return this.getEndpointForEntity(entity);
    }
  }

  async getConvertibleUnitGroups(): Promise<ConvertibleUnitGroup[]> {
    const data = await indexedDBManager.getReferenceData('convertibleUnitGroups');
    return data.map((item) => item.data as unknown as ConvertibleUnitGroup);
  }

  private getUniqueById<T extends { id?: number | string }>(items: T[]): T[] {
    const uniqueMap = new Map<number | string, T>();

    items.forEach((item) => {
      if (!item?.id) return;

      if (!uniqueMap.has(item.id)) {
        uniqueMap.set(item.id, item);
      }
    });

    return Array.from(uniqueMap.values());
  }

  async getEstablishments(): Promise<Establishment[]> {
    const data = await indexedDBManager.getReferenceData('establishments');
    const establishments = data
      .map((item) => item.data as unknown as Establishment)
      .filter((establishment) => establishment.isActive !== false);
    return this.getUniqueById(establishments).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }

  async getEstablishmentTypes(): Promise<any[]> {
    const data = await indexedDBManager.getReferenceData('establishmentTypes');
    const types = data
      .map((item) => item.data)
      .filter((type: any) => type.isActive !== false);
    return this.getUniqueById(types).sort((a: any, b: any) =>
      (a.nameEn || '').localeCompare(b.nameEn || '')
    );
  }

  async getTasks(): Promise<Task[]> {
    const data = await indexedDBManager.getReferenceData('task');
    const tasks = data
      .map((item) => item.data as unknown as Task)
      .filter((task) => task.isActive !== false);
    return this.getUniqueById(tasks).sort((a, b) =>
      (a.taskName || '').localeCompare(b.taskName || '')
    );
  }

  async getTaskById(taskId: string): Promise<Task | null> {
    try {
      const data = await indexedDBManager.getReferenceData('task');
      const task = data.find((item) => item.data.refId === taskId || item.data.id === taskId);
      if (task && task.data.isActive !== false) {
        return task.data as unknown as Task;
      }
      return null;
    } catch (error) {
      console.error('Error fetching task by ID:', error);
      return null;
    }
  }

  async getTaskTypes(): Promise<TaskTypeOption[]> {
    const rows = await indexedDBManager.getReferenceData('task_type');
    return rows
      .map((r) => r.data as any)
      .filter(Boolean)
      .map(
        (t): TaskTypeOption => ({
          id: Number(t.id),
          key: String(t.key),
          name: t.name != null ? String(t.name) : undefined,
          createTime: t.createTime,
          updateTime: t.updateTime,
        })
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  async getEstablishmentsByPosition(establishmentTypeKey: string): Promise<Establishment[]> {
    const allEstablishments = await this.getEstablishments();
    return allEstablishments.filter((establishment: any) => {
      const estabTypeKey = establishment.establishmentType?.key || establishment.establishmentType?.nameEn || establishment.establishmentType;
      return estabTypeKey === establishmentTypeKey;
    });
  }

  async getEstablishmentsByLocationAndPosition(
    establishmentTypeKey?: string,
    locationId?: number,
    department?: string
  ): Promise<Establishment[]> {
    const allEstablishments = await this.getEstablishments();
    const allLocations = department ? await this.getLocations() : [];

    return allEstablishments.filter((establishment: any) => {
      let matches = true;
      if (establishmentTypeKey) {
        const estabTypeKey =
          establishment.establishmentType?.key ||
          establishment.establishmentType?.nameEn ||
          establishment.establishmentType ||
          establishment.positionType;
        matches =
          matches &&
          String(estabTypeKey).toLowerCase() === String(establishmentTypeKey).toLowerCase();
      }
      if (locationId !== undefined) {
        matches = matches && Number(establishment.locationId) === Number(locationId);
      }
      if (department) {
        const location = allLocations.find(
          (loc: any) => Number(loc.id) === Number(establishment.locationId)
        );
        const estDept =
          location?.district ||
          establishment.department ||
          establishment.location?.district;
        matches =
          matches &&
          String(estDept).toLowerCase() === String(department).toLowerCase();
      }
      return matches;
    });
  }

  async getEstablishmentsId(
    typeOfPosition: string,
    establishmentName: string
  ): Promise<Establishment[]> {
    const allEstablishments = await this.getEstablishments();
    return allEstablishments.filter((establishment: any) => {
      const estabTypeKey = establishment.establishmentType?.key || establishment.establishmentType?.nameEn || establishment.establishmentType;
      return estabTypeKey === typeOfPosition && establishment.name === establishmentName;
    });
  }

  async getEstablishmentDataById(id: number): Promise<Establishment[]> {
    const allEstablishments = await this.getEstablishments();
    return allEstablishments.filter((establishment) => establishment.id === id);
  }

  async getLocations(): Promise<Record<string, unknown>[]> {
    const data = await indexedDBManager.getReferenceData('locations');
    return data
      .map((item) => item.data)
      .filter((location: any) => location.isActive !== false)
      .sort((a: any, b: any) => {
        const districtCompare = (a.district || '').localeCompare(b.district || '');
        return districtCompare || (a.city || '').localeCompare(b.city || '');
      });
  }

  async getLocationByCoordinates(lat: number, lng: number): Promise<any[]> {
    try {
      const authToken = this.getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept-Language': getLanguage(),
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const consumerPriceUrl = BASE_URL.CONSUMER_PRICE_BASE;
      const endpoint = `/${API_ENDPOINTS.LOCATION.GET_BY_COORDINATES}`;
      const url = new URL(`${consumerPriceUrl}${endpoint}`);
      url.searchParams.append('lat', lat.toString());
      url.searchParams.append('lng', lng.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.warn(`Failed to fetch location by coordinates: ${response.status}`);
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching location by coordinates:', error);
      return [];
    }
  }

  async getPrices(): Promise<Price[]> {
    const priceData = await indexedDBManager.getLocalData('price');

    return priceData
      .map(
        (item: { data: Record<string, unknown> }) =>
          (item.data as unknown as Price) || (item as unknown as Price)
      )
      .filter((price: { isActive: boolean }) => price.isActive !== false);
  }

  async getPricesByInspectionIds(refIds: string[]): Promise<Price[]> {
    const allPriceData = await indexedDBManager.getLocalData('price');

    const priceDataForInspections = allPriceData.filter(
      (price: { data: { inspectionRefId: string } }) =>
        refIds.includes(price?.data?.inspectionRefId)
    );

    const latestPriceForProduct = priceDataForInspections.reduce(
      (acc: Record<string, any>, cur: { data: any }) => {
        const product = cur.data.productRefId;

        if (!acc[product] || cur?.data?.reportedTime > acc[product]?.data?.reportedTime) {
          acc[product] = cur?.data;
        }
        return acc;
      },
      {}
    );

    const result = Object.values(latestPriceForProduct);
    return result as Price[];
  }

  async getInspections(): Promise<Inspection[]> {
    const inspectionData = await indexedDBManager.getLocalData('inspection');

    if (!Array.isArray(inspectionData)) {
      return [];
    }

    const inspections = inspectionData
      .map((item) => (item?.data || item) as Inspection)
      .filter((inspection) => inspection?.isActive !== false);

    return this.getUniqueById(inspections);
  }

  async getInspectionsByTaskId(taskRefId: string): Promise<Inspection[]> {
    const allInspectionData = await indexedDBManager.getLocalData('inspection');
    const inspectionsByTaskRefId = allInspectionData.filter((inspection: { data: Inspection }) => {
      return inspection.data?.taskRefId?.toString() === taskRefId;
    });
    return inspectionsByTaskRefId.map((item: { data: Inspection }) => item.data);
  }

  async getInspectionsByRefId(refId: string): Promise<Record<string, unknown> | null> {
    try {
      const data = await indexedDBManager.getLocalData('inspection');

      // Find the inspection by refId
      const inspectionItem = Array.isArray(data)
        ? data.find((item) => item.data.refId === refId)
        : null;

      if (inspectionItem && inspectionItem.data.isActive !== false) {
        return inspectionItem.data;
      }

      return null;
    } catch {
      return null;
    }
  }

  async getProductCategories(): Promise<ProductCategory[]> {
    const data = await indexedDBManager.getReferenceData('productCategories');
    const categories = data
      .map((item) => item.data as unknown as ProductCategory)
      .filter((category) => category.isActive !== false);

    return this.getUniqueById(categories).sort((a, b) => {
      const mainCategoryCompare = (a.mainCategory || '').localeCompare(b.mainCategory || '');
      const categoryCompare = (a.category || '').localeCompare(b.category || '');
      return mainCategoryCompare || categoryCompare;
    });
  }

  async getMainProducts(): Promise<MainProduct[]> {
    const data = await indexedDBManager.getReferenceData('mainProduct');
    const mainProducts = data
      .map((item) => item.data as unknown as MainProduct)
      .filter((mainProduct) => mainProduct.isActive !== false);

    return this.getUniqueById(mainProducts).sort((a, b) => a.name.localeCompare(b.name));
  }

  async getPresentations(): Promise<Presentation[]> {
    const data = await indexedDBManager.getLocalData('presentation');

    if (!Array.isArray(data)) {
      return [];
    }

    const activePresentations = Array.isArray(data)
      ? data
          .map((item) => item?.data || item)
          .filter(
            (presentation) =>
              presentation?.isActive !== false &&
              presentation?.status === PresentationStatusEnum.APPROVED
          )
      : [];

    return this.getUniqueById(activePresentations)
      .map((presentation) => ({
        ...presentation,
        name: presentation?.name,
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  async getProductDataById(refId: string): Promise<Record<string, unknown>[]> {
    const allPresentationData = await indexedDBManager.getLocalData('presentation');

    return allPresentationData.filter(
      (item: Record<string, unknown>) =>
        (item.data as Record<string, unknown>)?.refId?.toString() === refId
    );
  }

  async getPresentationsForFilters(basketId?: number | Array<number>): Promise<Presentation[]> {
    if (Array.isArray(basketId)) {
      if (basketId.length === 0) {
        return await this.getPresentations();
      }

      const presentationGroups = await Promise.all(
        basketId.map((id) => this.getPresentationsFromBasketData(id))
      );

      return this.getUniqueByRefId(presentationGroups.flat());
    }

    if (basketId) {
      return this.getUniqueByRefId(await this.getPresentationsFromBasketData(basketId));
    }
    return this.getUniqueByRefId(await this.getPresentations());
  }

  private async getPresentationsFromBasketData(id: number): Promise<Presentation[]> {
    const basketData = await this.getBasketDataById(id);
    const basket = basketData?.[0]?.data as BasketData | undefined;

    if (!basket?.mainProducts?.length) {
      return [];
    }

    const presentations = basket.mainProducts
      .flatMap((mainProduct) =>
        (mainProduct.products ?? [])
          .filter((product) => product?.refId && product?.isActive !== false)
          .map(
            (product) =>
              ({
                id: product.id ?? 0,
                refId: product.refId as string,
                name: product.name,
                mainProductName: mainProduct.name,
                unit: product.unit || mainProduct.unit || '',
                productCategoryId: product.productCategoryId || 0,
                brand: product.brand || '',
                presentation: product.presentation || '',
                isActive: product.isActive !== false,
                createTime: basket.createTime,
                updateTime: basket.updateTime,
              }) as Presentation
          )
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return this.getUniqueByRefId(presentations);
  }

  private getUniqueByRefId<T extends { refId?: string }>(items: T[]): T[] {
    return Array.from(new Map(items.map((item) => [item.refId, item])).values());
  }

  private normalizeBasketName(name?: string): string {
    return (name || '').trim().toLocaleLowerCase();
  }

  async getProductsByCategoryId(categoryId: number): Promise<Presentation[]> {
    const allPresentations = await this.getPresentations();

    return allPresentations.filter((presentation) => {
      if (presentation?.isActive === false) {
        return false;
      }

      return presentation?.productCategoryId === categoryId;
    });
  }

  async getMainProductsByCategoryId(categoryId: number): Promise<MainProduct[]> {
    const allMainProducts: MainProduct[] = await this.getMainProducts();

    return allMainProducts.filter((mainProduct: MainProduct) => {
      return mainProduct?.productCategory?.id === categoryId;
    });
  }

  async getLocalProduct(refId: string): Promise<Record<string, unknown> | null> {
    try {
      const data = await indexedDBManager.getLocalData('presentation');

      // Find the product by refId
      const productItem = Array.isArray(data)
        ? data.find((item) => item.data.refId === refId)
        : null;

      if (productItem && productItem.data.isActive !== false) {
        return productItem.data;
      }

      return null;
    } catch {
      return null;
    }
  }

  async getBaskets(): Promise<(Basket & { productCount: number })[]> {
    const data = await indexedDBManager.getLocalData('basket');

    const activeBaskets = Array.isArray(data)
      ? data.filter((item) => item?.data?.isActive !== false)
      : [];

    const basketsWithProductCount = activeBaskets
      .map((item) => {
        const productCount =
          item.data?.mainProducts?.reduce(
            (count: number, mainProduct: BasketMainProduct) =>
              count +
              (mainProduct.products ?? []).filter((product) => product?.isActive !== false).length,
            0
          ) ?? 0;
        const basketData = { ...item.data, productCount: productCount || 0 };
        return basketData as unknown as Basket & { productCount: number };
      })
      .filter((basket) => basket.id);

    return this.getUniqueById(basketsWithProductCount).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }

  async getBasicFoodBasket(): Promise<(Basket & { productCount: number }) | undefined> {
    const baskets = await this.getBaskets();

    const activeBaskets = baskets.filter((basket) => basket?.isActive !== false);

    if (activeBaskets.length === 0) {
      return undefined;
    }

    const configuredName = this.normalizeBasketName(ENVIRONMENT_CONFIG.BASIC_FOOD_BASKET_NAME);

    return (
      activeBaskets.find((basket) => this.normalizeBasketName(basket.name) === configuredName) ??
      activeBaskets[0]
    );
  }

  async getBasicFoodBasketId(): Promise<number | undefined> {
    return (await this.getBasicFoodBasket())?.id;
  }

  async getBasketDataById(id: number): Promise<Record<string, any>[]> {
    const baskets = await indexedDBManager.getLocalData('basket');

    return baskets.filter((item: { data: { id: number } }) => item?.data?.id === id);
  }

  async getBasketDataByIds(ids: number[]): Promise<Record<string, any>[]> {
    try {
      const data = await indexedDBManager.getLocalData('basket');

      if (!Array.isArray(data) || !ids?.length) {
        return [];
      }

      const idSet = new Set(ids);

      return data.filter(
        (item: { data?: { id?: number } }) => item?.data?.id && idSet.has(item.data.id)
      );
    } catch (error) {
      console.error('Failed to get basket data by ids:', error);
      return [];
    }
  }

  async getLocationsByDistrict(district: string): Promise<Record<string, unknown>[]> {
    const allLocations = await this.getLocations();
    return allLocations.filter((location) => (location.district as string) === district);
  }

  async getLocationId(district: string, city: string): Promise<any> {
    const allLocations = await this.getLocations();

    const filteredLocationData = allLocations.filter(
      (item) => (item.district as string) === district && (item.city as string) === city
    );

    return filteredLocationData ? filteredLocationData[0]?.id : undefined;
  }

  async getLocationById(id: number): Promise<any> {
    const allLocations = await this.getLocations();

    const filteredLocationData = allLocations.filter((item) => (item.id as number) === id);

    return filteredLocationData ? filteredLocationData[0] : undefined;
  }

  async getCategoriesByMainCategory(mainCategory: string): Promise<ProductCategory[]> {
    const allCategories = await this.getProductCategories();
    return allCategories.filter((category) => (category.mainCategory as string) === mainCategory);
  }

  async getSubCategoriesByCategory(category: string): Promise<ProductCategory[]> {
    const allCategories = await this.getProductCategories();
    return allCategories.filter((item) => (item.category as string) === category);
  }

  async getProductCategoryId(mainCategory: string, category: string): Promise<ProductCategory[]> {
    const allCategories = await this.getProductCategories();
    return allCategories.filter(
      (item) =>
        (item.mainCategory as string) === mainCategory && (item.category as string) === category
    );
  }

  async getProductCategoryDataById(id: string): Promise<ProductCategory[]> {
    const allCategories = await this.getProductCategories();
    return allCategories.filter((item) => item.id?.toString() === id);
  }

  async getProductCategoryById(id: number): Promise<ProductCategory | null> {
    try {
      const allCategories = await this.getProductCategories();
      const category = allCategories.find((item) => item.id === id);
      return category || null;
    } catch (error) {
      console.error('Error getting product category by ID:', error);
      return null;
    }
  }

  async forceSyncEntity(entity: string): Promise<SyncStatus> {
    if (!navigator.onLine) {
      const existingData = await indexedDBManager.getReferenceData(entity);
      return {
        entity,
        isSync: false,
        lastSyncTime: 0,
        itemCount: existingData.length,
        error: 'Cannot force sync while offline',
      };
    }

    await indexedDBManager.updateSyncMap(entity, {
      lastSyncTime: 0,
      syncStatus: 'pending',
    });

    return await this.syncEntity(entity);
  }

  async getSyncStatus(): Promise<SyncStatus[]> {
    const results: SyncStatus[] = [];

    for (const entity of this.entities) {
      const syncMap = await indexedDBManager.getSyncMap(entity);
      const data = await indexedDBManager.getReferenceData(entity);

      results.push({
        entity,
        isSync: syncMap?.syncStatus === 'synced',
        lastSyncTime: syncMap?.lastSyncTime || 0,
        itemCount: data.length,
        error: syncMap?.syncStatus === 'error' ? 'Sync error' : undefined,
      });
    }

    return results;
  }

  async getLastSyncTime(): Promise<{
    lastSyncTime: number;
    formattedTime: string;
    isOnline: boolean;
    syncStatus: 'synced' | 'pending' | 'error' | 'never';
  }> {
    let lastSyncTime = 0;
    let lastSyncOperationTime = 0;
    let hasErrors = false;
    let allSynced = true;

    for (const entity of this.entities) {
      const syncMap = await indexedDBManager.getSyncMap(entity);
      if (syncMap?.lastSyncTime && syncMap?.lastSyncTime > lastSyncTime) {
        lastSyncTime = syncMap.lastSyncTime;
      }
      if (
        syncMap?.lastSyncOperationTime &&
        syncMap?.lastSyncOperationTime > lastSyncOperationTime
      ) {
        lastSyncOperationTime = syncMap.lastSyncOperationTime;
      }
      if (syncMap?.syncStatus === 'error') {
        hasErrors = true;
      }
      if (syncMap?.syncStatus !== 'synced') {
        allSynced = false;
      }
    }

    const currentSyncTime = lastSyncOperationTime > 0 ? lastSyncOperationTime : lastSyncTime;

    let syncStatus: 'synced' | 'pending' | 'error' | 'never';
    if (hasErrors) {
      syncStatus = 'error';
    } else if (allSynced && currentSyncTime > 0) {
      syncStatus = 'synced';
      this.setLastFullSyncTime(currentSyncTime);
    } else if (currentSyncTime > 0) {
      syncStatus = 'pending';
    } else {
      syncStatus = 'never';
    }

    const displaySyncTime = syncStatus === 'pending' ? this.getLastFullSyncTime() : currentSyncTime;

    const formattedTime =
      displaySyncTime > 0 ? new Date(displaySyncTime).toLocaleString() : 'Never synced';

    return {
      lastSyncTime: displaySyncTime,
      formattedTime,
      isOnline: navigator.onLine,
      syncStatus,
    };
  }

  async clearAllData(): Promise<void> {
    for (const entity of this.entities) {
      const storeName = this.getStoreName(entity);
      const transaction = indexedDBManager['db']?.transaction([storeName], 'readwrite');
      if (transaction) {
        const store = transaction.objectStore(storeName);
        await indexedDBManager['promisifyRequest'](store.clear());
      }
    }

    const syncTransaction = indexedDBManager['db']?.transaction(['syncMaps'], 'readwrite');
    if (syncTransaction) {
      const syncStore = syncTransaction.objectStore('syncMaps');
      for (const entity of this.entities) {
        await indexedDBManager['promisifyRequest'](syncStore.delete(entity));
      }
    }

    try {
      localStorage.removeItem(this.lastFullSyncTimeStorageKey);
    } catch {
      // ignore storage errors
    }
  }

  updateConfig(newConfig: Partial<SyncConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  private setupConnectionListeners(): void {
    this.onlineHandler = async () => {
      if (this.isRunning) {
        await this.syncAllEntities();
      }
    };

    this.offlineHandler = () => {
      // Connection lost - will use cached reference data
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
  }

  private removeConnectionListeners(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = null;
    }
  }

  private getStoreName(entity: string): string {
    const storeNameMap: Record<string, string> = {
      presentation: 'presentations',
      basket: 'baskets',
      price: 'price',
      inspection: 'inspection',
      establishments: 'establishments',
      locations: 'locations',
      productCategories: 'productCategories',
      mainProduct: 'mainProducts',
    };

    return storeNameMap[entity] || entity + 's';
  }

  private async storeServerDataAsLocal(
    entity: string,
    items: Record<string, unknown>[]
  ): Promise<void> {
    const storeName = this.getStoreName(entity);

    const batchSize = 10;
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);

      const transaction = indexedDBManager['db']?.transaction([storeName], 'readwrite');
      if (!transaction) {
        throw new Error(`IndexedDB transaction unavailable for ${storeName}`);
      }

      const store = transaction.objectStore(storeName);

      const batchPromises = batch.map(async (item) => {
        try {
          await this.upsertServerItem(store, entity, item);
          await this.cleanupSyncQueueForItem(entity, item);
        } catch (error) {
          throw error;
        }
      });

      try {
        await Promise.all(batchPromises);
      } catch (error) {
        throw error instanceof Error
          ? error
          : new Error(`Failed to store ${entity} reference data`);
      }
    }
  }

  /**
   * Upsert a single server item using the same logic as backend upsert
   */
  private async upsertServerItem(
    store: IDBObjectStore,
    entity: string,
    item: Record<string, unknown>
  ): Promise<void> {
    let existingItem = null;
    let refId: string | number;

    const isOnlineFirst = ONLINE_FIRST_ENTITIES.has(entity);

    if (isOnlineFirst && item.id !== undefined && item.id !== null) {
      refId = item.id as number;
    } else if (item.refId) {
      refId = String(item.refId);
    } else {
      refId = generateUniqueRefId(entity);
      item = { ...item, refId };
    }

    try {
      if (isOnlineFirst && item.refId && String(item.refId) !== String(refId)) {
        await indexedDBManager['promisifyRequest'](store.delete(String(item.refId)));
      }
      existingItem = await indexedDBManager['promisifyRequest'](store.get(refId));
    } catch {
      existingItem = null;
    }

    if (existingItem) {
      const updatedData = {
        ...existingItem,
        data: {
          ...existingItem.data,
          ...item,
        },
        timestamp: Date.now(),
        synced: 1,
      };

      await indexedDBManager['promisifyRequest'](store.put(updatedData));
    } else {
      const localData = {
        id: refId,
        entity,
        data: item,
        timestamp: Date.now(),
        synced: 1,
      };

      await indexedDBManager['promisifyRequest'](store.put(localData));
    }
  }

  private async cleanupSyncQueueForItem(
    entity: string,
    item: Record<string, unknown>
  ): Promise<void> {
    try {
      const refId = item.refId || generateUniqueRefId(entity);

      const transaction = indexedDBManager['db']?.transaction(['syncQueue'], 'readwrite');
      if (!transaction) {
        return;
      }

      const store = transaction.objectStore('syncQueue');
      const index = store.index('entity');
      const request = index.getAll(IDBKeyRange.only(entity));
      const items = await indexedDBManager['promisifyRequest'](request);

      for (const syncItem of items) {
        if (syncItem.data.refId === refId && syncItem.synced === 0) {
          await indexedDBManager['promisifyRequest'](store.delete(syncItem.id));
        }
      }
    } catch (error) {
      console.error('Error cleaning up sync queue for item:', error);
    }
  }

  async checkBarcodeExists(barcode: string, excludeProductId?: string): Promise<boolean> {
    if (!barcode || barcode.trim() === '') {
      return false;
    }

    try {
      const allPresentations: Presentation[] = await this.getPresentations();
      const presentationWithBarcode = allPresentations.find((presentation: Presentation) => {
        // Check if barcode matches
        if (presentation.barcode === barcode.trim()) {
          // If excludeProductId is provided (for edit mode), allow the same product to keep its barcode
          if (excludeProductId && presentation.refId === excludeProductId) {
            return false;
          }
          return true;
        }
        return false;
      });

      return !!presentationWithBarcode;
    } catch (error) {
      console.error('Error checking barcode existence:', error);
      return false;
    }
  }
}

export const referenceDataSyncService = new ReferenceDataSyncService();
